import { ChevronDown, Lock, Trash2, Wallet } from 'lucide-react'
import type { ClipboardEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { CarrierSelect } from '@/features/cases/components/CarrierSelect'
import { normaliseDecimal } from '@/features/finance/lines'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber } from '@/lib/format'
import { fieldErrorKey } from '@/lib/problems'
import type { Money } from '@/types/domain'

import { RATE_CURRENCIES, type RowDefaults, type UnitRow } from './legRows'
import {
  carries,
  cellLabel,
  columnLabel,
  foldedFields,
  rowObject,
  rowTitle,
  serverFieldColumn,
  visibleColumns,
  type UnitColumn,
} from './unitKinds'

/* One unit of a unit card, in its two shapes (S-10; every kind since S-18b):
 * a row of inline inputs on a desk, a card that opens a bottom sheet on a
 * phone. ONE of the two is mounted (`useTableFits`), so a unit's controls are
 * never drawn twice.
 *
 * The columns are the row's KIND's (`UNIT_COLUMNS`): a truck has its trailer,
 * driver and phone, a container and a wagon a seal and a type, an air waybill
 * a weight, a B/L its number alone — and every kind its carrier, its rate and
 * the served «Остаток». Every control names its row («Водитель, строка 2»,
 * «Пломба, контейнер 1») — a grid of twenty identical «Водитель» boxes is
 * unusable with a screen reader — and nothing here writes: a change goes up
 * as a patch to the page's draft.
 */

/** What a row may do, decided by the card once for all of them. */
export interface RowAbilities {
  /** Carrier, driver and phone exist at all — `seesAgentIdentity === true`. */
  showIdentity: boolean
  /** This row offers a rate input: a new unit, or a saved one with no
   *  per-unit plan, on a leg whose money this reader may write. */
  ratable: boolean
  /** The unit's rate as already booked, read-only (AD-4: edited on the money
   *  screen), or as just written by this page. */
  bookedRate?: Money
  /** The rate line a failed press is holding for this unit: shown read-only
   *  as exactly what «Повторить» will send, until it lands or «Отмена» drops
   *  it — and the unit it names offers no remove control and a read-only
   *  number meanwhile. */
  heldRate?: Money
  /** What is still owed on this unit (`perUnit[].remaining`), served and
   *  read-only — printed under «Остаток» for every reader, Sales included
   *  (the figure is not the secret, the carrier is). Absent on an unsaved
   *  row and before the leg's money block is read. */
  remaining?: Money
  /** The unit carries money (0056): no remove control, the reason instead. */
  moneyHeld: boolean
  /** Offer the per-unit pay act. */
  canPay: boolean
  readOnly: boolean
  /** The rate's currency had no FX rate on the booking date (409
   *  `rate-missing`): the rate and its currency are marked. */
  rateMissing: boolean
}

/** The booked, held or just-written figure a row shows instead of an input. */
function shownRate(row: UnitRow, abilities: RowAbilities): string | null {
  if (abilities.bookedRate) {
    return formatMoney(abilities.bookedRate.amount, abilities.bookedRate.currency)
  }
  if (abilities.heldRate) {
    return formatMoney(abilities.heldRate.amount, abilities.heldRate.currency)
  }
  if (row.rateWritten && row.rate.trim() !== '') {
    return formatMoney(normaliseDecimal(row.rate), row.currency || 'USD')
  }
  return null
}

function shownRemaining(abilities: RowAbilities): string | null {
  return abilities.remaining ? formatMoney(abilities.remaining.amount, abilities.remaining.currency) : null
}

/** Whether the figure shown is a held line, not a booked one. */
function isHeld(abilities: RowAbilities): boolean {
  return abilities.heldRate !== undefined && abilities.bookedRate === undefined
}

/** Why a unit offers no remove control, as the i18n key pair of its reason —
 *  or null when it may be removed. Money on the unit (0056) comes first; then
 *  a held rate line: «Повторить» re-sends it for THIS unit id, so a unit
 *  removed under it would make the next batch name a unit the leg no longer
 *  has, and the all-or-none batch would refuse every other rate with it. */
function removeLock(abilities: RowAbilities) {
  if (abilities.moneyHeld) {
    return { reason: 'cases.legs.vehiclesTable.removeHeld', named: 'cases.legs.vehiclesTable.removeHeldNamed' } as const
  }
  if (abilities.heldRate !== undefined) {
    return {
      reason: 'cases.legs.vehiclesTable.removeRateHeld',
      named: 'cases.legs.vehiclesTable.removeRateHeldNamed',
    } as const
  }
  return null
}

/** Whether the number is frozen: a held rate line names THIS unit, and the
 *  units are upserted by kind and number — a retyped or cleared number is a
 *  different unit, or none, and the held line would name one the leg no
 *  longer has. */
function numberFrozen(abilities: RowAbilities): boolean {
  return abilities.heldRate !== undefined
}

/** The words under a held figure: that it is not recorded yet (or which
 *  currency had no rate), with what «Повторить» and «Отмена» do to it. */
function HeldNote({ abilities, full = false }: { abilities: RowAbilities; full?: boolean }) {
  const { t } = useTranslation()
  const short = abilities.rateMissing && abilities.heldRate
    ? t('cases.legs.vehiclesTable.rateHeldMissing', { currency: abilities.heldRate.currency })
    : t('cases.legs.vehiclesTable.rateHeld')
  const hint = t('cases.legs.vehiclesTable.rateHeldHint')
  return (
    <span
      title={full ? undefined : hint}
      className={cn('block text-[12px] leading-snug', abilities.rateMissing ? 'text-negative' : 'text-text-muted')}
    >
      {full ? `${short}. ${hint}` : short}
    </span>
  )
}

/** A served refusal of this row, worded under the field it names — «Госномер:
 *  Уже есть в списке» — rather than as a generic «Значение не принято» above
 *  the record (AC #14). */
function RowErrors({ row, errors }: { row: UnitRow; errors?: ReadonlyArray<{ field: string; code: string }> }) {
  const { t } = useTranslation()
  if (!errors || errors.length === 0) return null
  return (
    <span className="text-negative block text-[12px] leading-snug">
      {errors
        .map(({ field, code }) => `${columnLabel(t, row.kind, serverFieldColumn(field))}: ${t(fieldErrorKey(code))}`)
        .join(' · ')}
    </span>
  )
}

/** The text field a column edits, `null` for the columns that are not one. */
const TEXT_FIELD: Partial<Record<UnitColumn, 'number' | 'trailerPlate' | 'sealNumber' | 'unitType' | 'driverName' | 'driverPhone'>> = {
  number: 'number',
  trailer: 'trailerPlate',
  seal: 'sealNumber',
  unitType: 'unitType',
  driver: 'driverName',
  phone: 'driverPhone',
}

export function UnitGridRow({
  row,
  index,
  defaults,
  abilities,
  expanded,
  problems,
  errors,
  onChange,
  onPaste,
  onRemove,
  onToggle,
  onPay,
}: {
  row: UnitRow
  index: number
  defaults: RowDefaults
  abilities: RowAbilities
  expanded: boolean
  problems: { rate: boolean; weight: boolean }
  errors?: ReadonlyArray<{ field: string; code: string }>
  onChange: (patch: Partial<UnitRow>) => void
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => void
  onRemove: () => void
  onToggle: () => void
  onPay: () => void
}) {
  const { t } = useTranslation()
  const n = index + 1
  const columns = visibleColumns(row.kind, abilities.showIdentity)
  const label = (column: UnitColumn | 'cmr' | 'container') =>
    cellLabel(t, row.kind, columnLabel(t, row.kind, column), n)
  const object = rowObject(t, row, n)
  const title = rowTitle(t, row, n)
  const cell = 'px-1.5 py-1.5 align-top first:pl-5 last:pr-5'
  /* A blank row carries the defaults FAINTLY — its carrier, rate and currency
     are the header's until somebody types into it, at which point they become
     the row's own (`materialize`). Faint is the whole row, and focus brings it
     back to full strength so what is being typed is never faint. */
  const carrierId = row.pristine ? defaults.carrierId : row.carrierId
  const carrierName = row.pristine ? defaults.carrierName : row.carrierName
  const currency = (row.pristine ? defaults.currency : row.currency) || 'USD'
  const booked = shownRate(row, abilities)
  const remaining = shownRemaining(abilities)
  const lock = removeLock(abilities)
  const folded = foldedFields(row.kind)
  const colSpan = columns.length + 1
  const invalid = (column: UnitColumn | 'cmr' | 'container') =>
    errors?.some(({ field }) => serverFieldColumn(field) === column) || undefined

  const payButton =
    abilities.canPay && row.id !== '' ? (
      <IconButton
        size="sm"
        icon={Wallet}
        onClick={onPay}
        label={t('cases.legs.vehiclesTable.payRowNamed', { unit: object })}
      />
    ) : null

  if (abilities.readOnly) {
    const readCell = (column: UnitColumn): ReactNode => {
      switch (column) {
        case 'number':
          return <span className="text-text-heading font-semibold">{row.number}</span>
        case 'carrier':
          return row.carrierName || defaults.carrierName || '—'
        case 'weight':
          return row.weightKg ? formatNumber(row.weightKg) : '—'
        case 'rate':
          return booked ?? '—'
        case 'remaining':
          return remaining ?? '—'
        default: {
          const field = TEXT_FIELD[column]
          return (field && row[field]) || '—'
        }
      }
    }
    return (
      <tr className="border-line-hair border-b text-[13px] last:border-b-0">
        {columns.map((column) => (
          <td
            key={column}
            className={cn(
              cell,
              'text-text-body py-3',
              (column === 'weight' || column === 'rate' || column === 'remaining') && 'asa-num text-right',
              (column === 'rate' || column === 'remaining') && 'text-text-heading',
              column === 'phone' && 'break-all',
            )}
          >
            {readCell(column)}
          </td>
        ))}
        <td className={cn(cell, 'py-3 text-right')}>{payButton}</td>
      </tr>
    )
  }

  const editCell = (column: UnitColumn): ReactNode => {
    switch (column) {
      case 'number':
        return numberFrozen(abilities) ? (
          /* Read-only, not swapped for text: the number stays the row's named
             field, with the reason on hover and focus — and the row's lock
             already says it in words. */
          <Input
            inputSize="sm"
            value={row.number}
            aria-label={label('number')}
            readOnly
            title={t('cases.legs.vehiclesTable.plateHeld')}
            className="bg-surface-disabled"
          />
        ) : (
          <Input
            inputSize="sm"
            value={row.number}
            aria-label={label('number')}
            aria-invalid={invalid('number')}
            onChange={(event) => onChange({ number: event.target.value })}
            onPaste={onPaste}
          />
        )
      case 'carrier':
        return (
          <CarrierSelect
            size="sm"
            value={carrierId}
            name={carrierName}
            aria-label={label('carrier')}
            onChange={(carrier) => onChange({ carrierId: carrier.id, carrierName: carrier.name })}
          />
        )
      case 'weight':
        return (
          <Input
            inputSize="sm"
            inputMode="numeric"
            className="asa-num"
            value={row.weightKg}
            aria-label={label('weight')}
            aria-invalid={problems.weight || invalid('weight')}
            onChange={(event) => onChange({ weightKg: event.target.value })}
          />
        )
      case 'rate':
        return abilities.ratable && booked === null ? (
          <div className="flex items-start gap-1">
            <Input
              inputSize="sm"
              inputMode="decimal"
              className="asa-num min-w-0 flex-1"
              value={row.rate}
              placeholder={row.pristine ? defaults.rate : undefined}
              aria-label={label('rate')}
              aria-invalid={problems.rate || abilities.rateMissing || undefined}
              onChange={(event) => onChange({ rate: event.target.value })}
            />
            <Select
              selectSize="sm"
              className="w-[84px] shrink-0"
              value={currency}
              onChange={(next) => onChange({ currency: next })}
              options={RATE_CURRENCIES.map((code) => ({ value: code, label: code }))}
              aria-label={cellLabel(t, row.kind, t('cases.legs.vehiclesTable.currency'), n)}
              aria-invalid={abilities.rateMissing || undefined}
            />
          </div>
        ) : (
          <div className="pt-1.5 text-right">
            <span className="asa-num text-text-heading block">{booked ?? '—'}</span>
            {isHeld(abilities) ? <HeldNote abilities={abilities} /> : null}
          </div>
        )
      case 'remaining':
        /* Served and read-only (S-18b): what is still owed on this unit. */
        return <span className="asa-num text-text-body block pt-1.5 text-right">{remaining ?? '—'}</span>
      default: {
        const field = TEXT_FIELD[column]
        if (!field) return null
        return (
          <Input
            inputSize="sm"
            inputMode={field === 'driverPhone' ? 'tel' : undefined}
            value={row[field]}
            aria-label={label(column)}
            aria-invalid={invalid(column)}
            onChange={(event) => onChange({ [field]: event.target.value })}
          />
        )
      }
    }
  }

  return (
    <>
      <tr
        className={cn(
          'border-line-hair border-b text-[13px]',
          row.pristine && 'opacity-60 focus-within:opacity-100',
          abilities.rateMissing && 'bg-negative-soft',
        )}
      >
        {columns.map((column) => (
          <td key={column} className={cn(cell, (column === 'rate' || column === 'remaining') && 'whitespace-nowrap')}>
            {editCell(column)}
          </td>
        ))}
        <td className={cn(cell, 'whitespace-nowrap')}>
          <div className="flex items-center justify-end gap-0.5 pt-0.5">
            {/* A truck's CMR and container, one press away. Not columns: two
                more inputs per row is the ninth and tenth column on a desk
                that mostly leaves them blank. Other kinds fold nothing. */}
            {folded.length > 0 ? (
              <IconButton
                size="sm"
                icon={ChevronDown}
                aria-expanded={expanded}
                className={cn('transition-transform', expanded && 'rotate-180')}
                label={t('cases.legs.vehiclesTable.moreFieldsNamed', { plate: row.number.trim() || title })}
                onClick={onToggle}
              />
            ) : null}
            {payButton}
            {row.pristine ? null : lock ? (
              /* No remove on a unit that carries money (0056 restricts the
                 delete: a control that could only be refused is not offered)
                 or whose rate line a failed press holds. The reason is where
                 the control would be, spoken and hovered — never a bare
                 missing button. */
              <span
                role="img"
                aria-label={t(lock.named, { unit: object, label: title })}
                title={t(lock.reason)}
                className="text-text-muted flex size-[30px] items-center justify-center"
              >
                <Lock size={14} aria-hidden />
              </span>
            ) : (
              <IconButton
                size="sm"
                icon={Trash2}
                className="hover:text-negative"
                label={t('cases.legs.vehiclesTable.removeRowNamed', { unit: object })}
                onClick={onRemove}
              />
            )}
          </div>
        </td>
      </tr>
      {errors && errors.length > 0 ? (
        <tr className="border-line-hair border-b">
          <td colSpan={colSpan} className="px-5 pt-0 pb-2">
            <RowErrors row={row} errors={errors} />
          </td>
        </tr>
      ) : null}
      {expanded && folded.length > 0 ? (
        <tr className="border-line-hair border-b">
          <td colSpan={colSpan} className="px-5 pt-1 pb-3">
            <div className="grid max-w-xl gap-3 sm:grid-cols-2">
              {folded.map((field) => {
                const column = field === 'cmrNumber' ? 'cmr' : 'container'
                return (
                  <label key={field} className="flex min-w-0 flex-col gap-1">
                    <span className="text-text-muted text-xs">{columnLabel(t, row.kind, column)}</span>
                    <Input
                      inputSize="sm"
                      className="font-mono"
                      value={row[field]}
                      aria-label={label(column)}
                      onChange={(event) => onChange({ [field]: event.target.value })}
                    />
                  </label>
                )
              })}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** A unit on a phone: the number and its rate, then who runs it and the
 *  remainder. The whole card is the button that opens the editor. */
export function UnitCard({
  row,
  index,
  defaults,
  abilities,
  errors,
  onOpen,
}: {
  row: UnitRow
  index: number
  defaults: RowDefaults
  abilities: RowAbilities
  errors?: ReadonlyArray<{ field: string; code: string }>
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const title = rowTitle(t, row, index + 1)
  const rate = shownRate(row, abilities) ??
    (row.rate.trim() !== '' && abilities.ratable
      ? formatMoney(normaliseDecimal(row.rate), row.currency || 'USD')
      : null)
  const remaining = shownRemaining(abilities)
  const who = abilities.showIdentity
    ? [row.carrierName || defaults.carrierName, carries(row.kind, 'driverName') ? row.driverName : ''].filter(
        (part) => part.trim() !== '',
      )
    : []
  const extra = [
    carries(row.kind, 'sealNumber') && row.sealNumber ? row.sealNumber : '',
    carries(row.kind, 'unitType') && row.unitType ? row.unitType : '',
  ].filter((part) => part !== '')
  const content = (
    <>
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-text-heading font-semibold break-all">{row.number.trim() || title}</span>
        {rate ? (
          <span className="asa-num text-text-heading shrink-0">{rate}</span>
        ) : null}
      </span>
      {isHeld(abilities) ? <HeldNote abilities={abilities} /> : null}
      {extra.length > 0 ? (
        <span className="text-text-muted block text-[12.5px] [overflow-wrap:anywhere]">{extra.join(' · ')}</span>
      ) : null}
      {who.length > 0 ? (
        <span className="text-text-muted block text-[12.5px] [overflow-wrap:anywhere]">
          {who.join(' · ')}
        </span>
      ) : null}
      {row.weightKg && carries(row.kind, 'weightKg') ? (
        <span className="text-text-muted asa-num block text-[12.5px]">
          {t('cases.legs.vehiclesTable.weightKg', { weight: formatNumber(row.weightKg) })}
        </span>
      ) : null}
      {remaining ? (
        <span className="text-text-muted block text-[12.5px]">
          {t('cases.legs.units.columns.remaining')}: <span className="asa-num text-text-body">{remaining}</span>
        </span>
      ) : null}
      <RowErrors row={row} errors={errors} />
    </>
  )

  if (abilities.readOnly) {
    return (
      <li className={cn('border-border-subtle flex flex-col gap-1 rounded-md border px-4 py-3', abilities.rateMissing && 'bg-negative-soft')}>
        {content}
      </li>
    )
  }
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('cases.legs.vehiclesTable.editRowNamed', { unit: rowObject(t, row, index + 1) })}
        className={cn(
          'border-border-subtle hover:bg-surface-hover flex w-full cursor-pointer flex-col gap-1 rounded-md border px-4 py-3 text-left',
          abilities.rateMissing && 'bg-negative-soft',
        )}
      >
        {content}
      </button>
    </li>
  )
}

/** The phone's editor for one unit — a bottom sheet over the cards. «Готово»
 *  hands the row back to the page's draft and writes nothing; the page's save
 *  bar writes. It asks the row's kind's fields and nothing else. */
export function UnitRowSheet({
  open,
  row,
  index,
  defaults,
  abilities,
  onClose,
  onDone,
  onRemove,
}: {
  open: boolean
  row: UnitRow
  index: number
  defaults: RowDefaults
  abilities: RowAbilities
  onClose: () => void
  onDone: (row: UnitRow) => void
  onRemove?: () => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useFormDraft<UnitRow>(open ? row.key : null, () => row)
  const set = (patch: Partial<UnitRow>) => setDraft({ ...draft, ...patch })
  const booked = shownRate(draft, abilities)
  const remaining = shownRemaining(abilities)
  const lock = removeLock(abilities)
  const title = rowTitle(t, row, index + 1)
  const col = (column: UnitColumn | 'cmr' | 'container') => columnLabel(t, row.kind, column)

  /* `frozen` is the reason a field is read-only — written out under it, since
     nothing hovers on a phone. */
  const text = (
    field: 'number' | 'trailerPlate' | 'containerNumber' | 'cmrNumber' | 'sealNumber' | 'unitType' | 'driverName' | 'driverPhone',
    name: UnitColumn | 'cmr' | 'container',
    frozen?: string,
  ) => (
    <label key={field} className="flex min-w-0 flex-col gap-1.5">
      <span className="text-text-muted text-xs">{col(name)}</span>
      <Input
        value={draft[field]}
        aria-label={col(name)}
        inputMode={field === 'driverPhone' ? 'tel' : undefined}
        readOnly={frozen !== undefined}
        className={frozen !== undefined ? 'bg-surface-disabled' : undefined}
        onChange={frozen !== undefined ? undefined : (event) => set({ [field]: event.target.value })}
      />
      {frozen !== undefined ? <span className="text-text-muted text-[12px] leading-snug">{frozen}</span> : null}
    </label>
  )
  const kind = row.kind

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={() => onDone(draft)}>{t('cases.legParams.done')}</Button>
          {onRemove ? (
            lock ? (
              <p className="text-text-muted text-[12.5px] leading-snug">{t(lock.reason)}</p>
            ) : (
              <Button variant="danger" iconLeft={Trash2} onClick={onRemove}>
                {t('cases.legs.vehiclesTable.removeRow', {
                  unit: t(`cases.legs.units.noun.${kind}` as 'cases.legs.units.noun.truck'),
                })}
              </Button>
            )
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {text('number', 'number', numberFrozen(abilities) ? t('cases.legs.vehiclesTable.plateHeld') : undefined)}
        {carries(kind, 'trailerPlate') ? text('trailerPlate', 'trailer') : null}
        {carries(kind, 'sealNumber') ? text('sealNumber', 'seal') : null}
        {carries(kind, 'unitType') ? text('unitType', 'unitType') : null}
        {abilities.showIdentity ? (
          <>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="text-text-muted text-xs">{col('carrier')}</span>
              <CarrierSelect
                value={draft.pristine ? defaults.carrierId : draft.carrierId}
                name={draft.pristine ? defaults.carrierName : draft.carrierName}
                aria-label={col('carrier')}
                onChange={(carrier) => set({ carrierId: carrier.id, carrierName: carrier.name })}
              />
            </div>
            {carries(kind, 'driverName') ? text('driverName', 'driver') : null}
            {carries(kind, 'driverPhone') ? text('driverPhone', 'phone') : null}
          </>
        ) : null}
        {carries(kind, 'weightKg') ? (
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-text-muted text-xs">{col('weight')}</span>
            <Input
              inputMode="numeric"
              className="asa-num"
              value={draft.weightKg}
              aria-label={col('weight')}
              onChange={(event) => set({ weightKg: event.target.value })}
            />
          </label>
        ) : null}
        {abilities.ratable && booked === null ? (
          <div className="flex items-end gap-2">
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className="text-text-muted text-xs">{col('rate')}</span>
              <Input
                inputMode="decimal"
                className="asa-num"
                value={draft.rate}
                aria-label={col('rate')}
                aria-invalid={abilities.rateMissing || undefined}
                onChange={(event) => set({ rate: event.target.value })}
              />
            </label>
            <Select
              className="w-24 shrink-0"
              value={draft.currency || defaults.currency || 'USD'}
              onChange={(currency) => set({ currency })}
              options={RATE_CURRENCIES.map((code) => ({ value: code, label: code }))}
              aria-label={t('cases.legs.vehiclesTable.currency')}
              aria-invalid={abilities.rateMissing || undefined}
            />
          </div>
        ) : booked ? (
          <div className="flex flex-col gap-0.5">
            <p className="text-text-body text-[13px]">
              {col('rate')}: <span className="asa-num">{booked}</span>
            </p>
            {isHeld(abilities) ? <HeldNote abilities={abilities} full /> : null}
          </div>
        ) : null}
        {remaining ? (
          <p className="text-text-body text-[13px]">
            {col('remaining')}: <span className="asa-num">{remaining}</span>
          </p>
        ) : null}
        {foldedFields(kind).map((field) => text(field, field === 'cmrNumber' ? 'cmr' : 'container'))}
      </div>
    </Sheet>
  )
}
